import React, { useState } from 'react'
import { pdfjs, Document, Page } from 'react-pdf'
import 'react-pdf/dist/esm/Page/AnnotationLayer.css'

import LoadingIndicator from 'components/LoadingIndicator'
import { isAndroidDevice, isIpadDevice } from 'utils/helper'

import { PdfViewer, CenteredMessage } from './styles'

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`

export type AttachmentPreviewPageState = 'loading' | 'ready' | 'error'

export type AttachmentPreviewProps = {
  pageState: AttachmentPreviewPageState
  /** 取得済みファイルの objectURL（ready 時のみ有効） */
  fileUrl: string
  fileName: string
  isPdf: boolean
  /** プレビュー対応拡張子か（非対応はダウンロードリンクを表示） */
  isPreviewable: boolean
}

/**
 * 添付ファイルのプレビュー表示（PDF / 画像 / ダウンロードフォールバック）。
 *
 * ファイルの取得（認証方式やエンドポイント）は呼び出し側のコントローラーに委ね、
 * 本コンポーネントは取得済みの objectURL を受け取って描画のみを担う。
 * ドライバー向け（SP）と出荷元担当者向け（PC 納品実績詳細）で共有する。
 */
export const AttachmentPreview: React.FC<AttachmentPreviewProps> = ({
  pageState,
  fileUrl,
  fileName,
  isPdf,
  isPreviewable,
}) => {
  const [pdfTotalPage, setPdfTotalPage] = useState<number | null>(null)

  if (pageState === 'loading') return <LoadingIndicator />
  if (pageState === 'error') {
    return <CenteredMessage>ファイルの取得に失敗しました。</CenteredMessage>
  }

  // プレビュー非対応（xls / xlsx 等）はダウンロードリンクを提示する
  if (!isPreviewable) {
    return (
      <CenteredMessage>
        <p>このファイルはプレビューに対応していません。</p>
        <a href={fileUrl} download={fileName}>
          {fileName} をダウンロード
        </a>
      </CenteredMessage>
    )
  }

  if (isPdf) {
    // モバイルは iframe で PDF をインライン表示できないため react-pdf でレンダリングする
    if (isIpadDevice || isAndroidDevice) {
      return (
        <PdfViewer>
          <Document
            file={fileUrl}
            onLoadSuccess={({ numPages }) => setPdfTotalPage(numPages)}
            noData=""
          >
            {pdfTotalPage &&
              Array(pdfTotalPage)
                .fill(null)
                .map((_, idx) => <Page key={idx} pageNumber={idx + 1} loading="" />)}
          </Document>
        </PdfViewer>
      )
    }

    return (
      <iframe
        title="attachment preview"
        src={fileUrl}
        style={{ width: '100vw', height: '100vh', border: 0 }}
      />
    )
  }

  return (
    <div style={{ textAlign: 'center' }}>
      <img alt={fileName} src={fileUrl} style={{ maxWidth: '100%' }} />
    </div>
  )
}
